export { SectionCard } from "../../../../components/common/section-card";
export type { SectionCardProps } from "../../../../components/common/section-card";

import type { ReactNode } from "react";
import { clsx } from "clsx";
import { IconClock } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";

interface FieldProps {
  label: string;
  value: unknown;
  /**
   * When set, renders an amber inline annotation under the value showing the
   * pending submitted change. Pass the formatted display string (the caller
   * resolves IDs to names where needed).
   */
  pending?: string | null;
  /** Inline control at the end of the value box (e.g. a "Change" button). */
  action?: ReactNode;
}

/**
 * Read-only labelled value. Long values (no spaces included) wrap inside the
 * box instead of widening the grid cell, so nothing spills out of its card.
 */
export function Field({ label, value, pending, action }: FieldProps) {
  const normalized =
    typeof value === "string"
      ? value.trim()
      : value === null || value === undefined || value === false
        ? ""
        : String(value).trim();
  const display = normalized ? normalized : "-";
  const showPending = pending !== null && pending !== undefined && pending !== "";
  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5">
      <span className="px-1.5">
        <Text variant="bodySmBold" inline tone="body">{label}</Text>
      </span>
      <div
        className={clsx(
          "flex min-h-[54px] items-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-3",
          // Keeps a box with a button as tall as its neighbours.
          action ? "py-2" : "py-4",
        )}
      >
        <Text variant="body" tone="muted" className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {display}
        </Text>
        {action}
      </div>
      {showPending && (
        <div className="flex items-start gap-1.5 px-1.5 pt-0.5">
          <IconClock size={14} className="mt-0.5 shrink-0 text-accentAmber" />
          <Text variant="bodyXs" tone="amber" className="min-w-0 [overflow-wrap:anywhere]">
            Pending: <span className="font-medium">{pending}</span> — awaiting approval
          </Text>
        </div>
      )}
    </div>
  );
}
